import React, { useState, useCallback } from 'react';
import { BaseAccountForm } from './BaseAccountForm';
import { AccountFormField } from '../ui/AccountFormField';
import { PasswordStrengthIndicator } from '../ui/PasswordStrengthIndicator';
import { validatePassword, validatePasswordConfirmation } from '../../utils/accountValidation';
import { ValidationResult } from '../../utils/validation';
import { usePasswordChange } from '../../hooks/usePasswordChange';
import { Key } from 'lucide-react';

export interface PasswordChangeFormProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  className?: string;
}

interface FormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ValidationState {
  currentPassword: ValidationResult;
  newPassword: ValidationResult;
  confirmPassword: ValidationResult;
}

/**
 * Password change form component with validation and strength indicator
 * Handles current password verification, new password validation, and confirmation matching
 */
export const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  onSuccess,
  onError,
  className
}) => {
  const [formState, setFormState] = useState<FormState>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [validationState, setValidationState] = useState<ValidationState>({
    currentPassword: { isValid: true },
    newPassword: { isValid: true },
    confirmPassword: { isValid: true }
  });

  const {
    changePassword,
    isSubmitting,
    error,
    success,
    clearError,
    clearSuccess
  } = usePasswordChange();

  // Update form field value
  const updateField = useCallback((field: keyof FormState, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    
    // Clear messages when user starts typing
    if (error) clearError();
    if (success) clearSuccess();
  }, [error, success, clearError, clearSuccess]);

  // Update validation state
  const updateValidation = useCallback((field: keyof ValidationState, result: ValidationResult) => {
    setValidationState(prev => ({ ...prev, [field]: result }));
  }, []);

  // Validate current password (basic non-empty check)
  const validateCurrentPassword = useCallback((password: string): ValidationResult => {
    if (!password.trim()) {
      return {
        isValid: false,
        error: 'Current password is required'
      };
    }
    return { isValid: true };
  }, []);

  // Validate new password confirmation
  const validateNewPasswordConfirmation = useCallback((confirmation: string): ValidationResult => {
    return validatePasswordConfirmation(formState.newPassword, confirmation);
  }, [formState.newPassword]);

  // Check if form is valid
  const isFormValid = () => {
    const { currentPassword, newPassword, confirmPassword } = validationState;
    const hasAllFields = formState.currentPassword && formState.newPassword && formState.confirmPassword;
    const allFieldsValid = currentPassword?.isValid && newPassword?.isValid && confirmPassword?.isValid;
    
    return hasAllFields && allFieldsValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      return;
    }

    try {
      await changePassword(formState.currentPassword, formState.newPassword, formState.confirmPassword);

      // Reset form on success
      setFormState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setValidationState({
        currentPassword: { isValid: true },
        newPassword: { isValid: true },
        confirmPassword: { isValid: true }
      });

      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change password';
      onError(errorMessage);
    }
  };

  return (
    <BaseAccountForm
      title="Change Password"
      description="Update your account password. You'll need to enter your current password to confirm the change."
      icon={<Key className="w-5 h-5" />}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      submitText="Change Password"
      submitDisabled={!isFormValid()}
      error={error}
      success={success}
      onClearError={clearError}
      onClearSuccess={clearSuccess}
      className={className}
    >
      <div className="space-y-6">
        {/* Current Password Field */}
        <AccountFormField
          id="current-password"
          label="Current Password"
          type="password"
          value={formState.currentPassword}
          onChange={(value) => updateField('currentPassword', value)}
          onValidation={(result) => updateValidation('currentPassword', result)}
          validator={validateCurrentPassword}
          placeholder="Enter your current password"
          required
          autoComplete="current-password"
          showPasswordToggle
          helperText="Enter your current password to verify your identity"
        />

        {/* New Password Field */}
        <div className="space-y-3">
          <AccountFormField
            id="new-password"
            label="New Password"
            type="password"
            value={formState.newPassword}
            onChange={(value) => updateField('newPassword', value)}
            onValidation={(result) => updateValidation('newPassword', result)}
            validator={validatePassword}
            placeholder="Enter your new password"
            required
            autoComplete="new-password"
            showPasswordToggle
            helperText="Choose a strong password with at least 6 characters"
          />

          {/* Password Strength Indicator */}
          <PasswordStrengthIndicator
            password={formState.newPassword}
            showLabel
            showRequirements
          />
        </div>

        {/* Confirm Password Field */}
        <AccountFormField
          id="confirm-password"
          label="Confirm New Password"
          type="password"
          value={formState.confirmPassword}
          onChange={(value) => updateField('confirmPassword', value)}
          onValidation={(result) => updateValidation('confirmPassword', result)}
          validator={validateNewPasswordConfirmation}
          placeholder="Confirm your new password"
          required
          autoComplete="new-password"
          showPasswordToggle
          helperText="Re-enter your new password to confirm"
        />
      </div>
    </BaseAccountForm>
  );
};